import { peekBranding } from "@/lib/branding";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLiveTick } from "@/hooks/useLiveTick";
import { Pencil, Save, X, User as UserIcon, TrendingUp, Download, Mail, Camera, Eye, EyeOff, Volume2, VolumeX, ChevronDown, ChevronUp, Wallet, FileText, History } from "lucide-react";
import PhotoCropDialog from "@/components/dashboard/PhotoCropDialog";
import VacationWaiverDialog from "@/components/dashboard/VacationWaiverDialog";
import VacationBankHistoryDialog from "@/components/dashboard/VacationBankHistoryDialog";
import ClockCard from "@/components/dashboard/ClockCard";
import OnboardingCard from "@/components/dashboard/OnboardingCard";
import NotificationBell from "@/components/dashboard/NotificationBell";
import CertificatesStrip from "@/components/dashboard/CertificatesStrip";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const hrs = (n) => `${Number(n || 0).toFixed(2)} h`;

// Employment fields set by admin — shown to the employee but never editable.
// Rendered only in the read-only view; hidden entirely in the edit form.
const READ_ONLY_GROUPS = [
  {
    title: "Employment (set by admin)",
    fields: [
      ["job_title", "Job title"],
      ["trade", "Trade"],
      ["employment_type", "Type", (v) => (v === "part_time" ? "Part-time" : v === "full_time" ? "Full-time" : v || "—")],
      ["status", "Status"],
      ["hired_at", "Employed from"],
      ["pay_type", "Pay type", (v) => (v === "salary" ? "Salary" : v === "hourly" ? "Hourly" : v || "—")],
      ["hourly_rate", "Hourly rate", (v) => (v ? `$${Number(v).toFixed(2)}/hr` : "—")],
      ["hourly_rate_perfect", "Perfect-attendance rate", (v) => (v ? `$${Number(v).toFixed(2)}/hr` : "—")],
      ["weekly_salary", "Weekly salary", (v) => (v ? `$${Number(v).toFixed(2)}/wk` : "—")],
      ["overtime_exempt", "Overtime exempt", (v) => (v ? "Yes" : "No")],
      ["vacation_opt_out", "Vacation opt-out", (v) => (v ? "Yes" : "No")],
    ],
  },
];

// Fields the user can edit on themselves. Grouped for the form UI.
const EDIT_GROUPS = [
  {
    title: "Contact",
    fields: [
      ["name", "Full name"],
      ["phone", "Phone"],
      ["email", "Email"],
    ],
  },
  {
    title: "Address",
    fields: [
      ["address_street", "Street"],
      ["address_city", "City"],
      ["address_postal", "Postal code"],
      ["date_of_birth", "Date of birth", "date"],
    ],
  },
  {
    title: "Emergency contact 1",
    fields: [
      ["emergency_contact_name", "Name"],
      ["emergency_contact_relationship", "Relationship"],
      ["emergency_contact_phone", "Phone"],
    ],
  },
  {
    title: "Emergency contact 2",
    fields: [
      ["emergency_contact_2_name", "Name"],
      ["emergency_contact_2_relationship", "Relationship"],
      ["emergency_contact_2_phone", "Phone"],
    ],
  },
  {
    title: "Licence & sizing",
    fields: [
      ["license_number", "Licence #"],
      ["license_expiry", "Licence expiry", "date"],
      ["shirt_size", "Shirt size"],
      ["allergies", "Allergies"],
    ],
  },
  {
    title: "Government ID",
    fields: [
      ["sin", "SIN"],
    ],
  },
  {
    title: "Direct deposit",
    fields: [
      ["bank_transit", "Transit / branch"],
      ["bank_institution", "Institution"],
      ["bank_account", "Account #"],
    ],
  },
];

const StatBox = ({ label, value, sub, testid, tint, action }) => (
  <div data-testid={testid} className={`rounded-lg border p-4 ${tint || "border-white/10 bg-white/[0.03]"}`}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</p>
    <p className="mt-1 text-2xl font-bold text-white">{value}</p>
    {sub && <p className="mt-1 font-mono text-[11px] text-white/50">{sub}</p>}
    {action}
  </div>
);

// Read-only CRA outstanding-debt card shown ONLY on the owner Home tab.
// Pulls live from /api/qbo/tax-summary so the numbers always match QBO.
//
// Special case: Colin (owner) sees a looped video instead of the warning.
// The video is cached in the browser CacheStorage after first fetch so it
// never re-downloads.
const COLIN_VIDEO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_secure-signin-43/artifacts/lscurilj_grok_video_2026-08-25-14-27-02.mp4";
const COLIN_VIDEO_CACHE = "elite-owner-media-v1";

const useCachedVideo = (url) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancel = false;
    let objectUrl = null;
    (async () => {
      try {
        const cache = await caches.open(COLIN_VIDEO_CACHE);
        let res = await cache.match(url);
        if (!res) {
          res = await fetch(url, { credentials: "omit", mode: "cors" });
          if (res && res.ok) {
            try { await cache.put(url, res.clone()); } catch { /* opaque or quota */ }
          }
        }
        if (cancel || !res || !res.ok) return;
        const blob = await res.blob();
        if (cancel) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        // Fall back to the direct URL — the browser will still handle its own HTTP cache.
        if (!cancel) setBlobUrl(url);
      }
    })();
    return () => {
      cancel = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return blobUrl;
};

const ColinOwnerVideo = () => {
  const src = useCachedVideo(COLIN_VIDEO_URL);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true); // required for browser autoplay
  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    setMuted(next);
    // Some browsers pause the video when we programmatically unmute — nudge it.
    if (!next) { try { el.play(); } catch { /* ignore */ } }
  };
  return (
    <div className="relative overflow-hidden rounded-lg border-2 border-red-500/40 bg-black" data-testid="owner-cra-colin-video">
      {src ? (
        <>
          <video
            ref={videoRef}
            src={src}
            autoPlay
            loop
            muted={muted}
            playsInline
            controls={false}
            onClick={toggleSound}
            className="block h-auto w-full cursor-pointer"
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleSound(); }}
            data-testid="owner-cra-colin-sound-toggle"
            aria-label={muted ? "Unmute" : "Mute"}
            style={{ color: "#ffffff", backgroundColor: "rgba(0,0,0,0.85)", textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full border border-white/30 px-3 py-2 text-xs font-bold shadow-lg backdrop-blur"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            <span style={{ color: "#ffffff" }}>{muted ? "Tap for sound" : "Mute"}</span>
          </button>
        </>
      ) : (
        <div className="flex h-40 items-center justify-center text-xs text-white/40">Loading…</div>
      )}
    </div>
  );
};

const isColinUser = (user) => {
  if (!user) return false;
  const match = (obj = {}) => {
    const name = (obj.name || "").trim().toLowerCase();
    const email = (obj.email || "").trim().toLowerCase();
    const owner = (obj.assigned_owner || "").trim().toLowerCase();
    return name.startsWith("colin") || email.startsWith("colin") || owner === "colin";
  };
  // Match on the current user (their own account OR an owner account whose
  // `assigned_owner` is Colin — e.g. Brittany Rainford linked to Colin) OR
  // — while impersonating — the real logged-in user.
  return match(user) || match(user.real_user || {});
};

// CRA position block. Rendered on the Reports tab (imported there) — used to
// live on My Stats until the user asked for the move (2026-09-01).
export const OwnerCraWarning = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  // Colin gets the video regardless of role or impersonation state.
  const showColinVideo = isColinUser(user);

  useEffect(() => {
    if (showColinVideo) return undefined; // skip API for Colin — video only
    let cancel = false;
    api.get("/qbo/tax-summary")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => {
        if (cancel) return;
        // 400 = QBO not connected — silent (nothing to show).
        if (e?.response?.status === 400) setErr("not-connected");
        else setErr(e?.response?.data?.detail || "load-failed");
      });
    return () => { cancel = true; };
  }, [showColinVideo]);

  if (showColinVideo) return <ColinOwnerVideo />;

  if (err === "not-connected") return null;
  if (!data && !err) {
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-500/[0.06] p-4" data-testid="owner-cra-loading">
        <p className="font-mono text-[10px] uppercase tracking-widest text-red-300">CRA position · loading…</p>
      </div>
    );
  }
  if (err) return null;

  const t = data?.totals || {};
  const owedTotal = Number(t.total_owed_to_cra || 0);
  const isOwing = owedTotal > 0;

  return (
    <div
      data-testid="owner-cra-warning"
      className={`rounded-lg border-2 p-4 ${
        isOwing
          ? "border-red-500/60 bg-gradient-to-br from-red-500/[0.14] to-red-500/[0.04]"
          : "border-emerald-500/40 bg-emerald-500/[0.06]"
      }`}
    >
      {isOwing && (
        <div className="mb-3 rounded-md border border-red-500/50 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100">
          🚨 THIS MUST BE PAID ASAP. Absolutely no spending on company debit cards. Even gas.
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-red-300">Total owed to CRA</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${isOwing ? "text-red-200" : "text-emerald-200"}`}
             data-testid="cra-total-owed">
            ${Number(owedTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="mt-0.5 text-[11px] text-red-300/70">Account balances + unpaid CRA bills</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">From tax accounts</p>
          <p className="mt-1 font-mono text-2xl font-bold text-amber-200">
            ${Number(t.owed_from_accounts || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="mt-0.5 text-[11px] text-amber-300/70">HST + payroll + income tax accumulated</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
            YTD paid to CRA {t.ytd_year ? `(${t.ytd_year})` : ""}
          </p>
          <p className="mt-1 font-mono text-2xl font-bold text-emerald-200">
            ${Number(t.ytd_paid_to_cra || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="mt-0.5 text-[11px] text-emerald-300/70">Bills + purchases settled this year</p>
        </div>
      </div>
    </div>
  );
};

/** Version + release stamp of the ACTUALLY LOADED bundle. The deploy stamps
 *  public/version.json at image build time, so an old browser session shows
 *  ITS OWN version — comparing it with what the server reports is how you
 *  tell a stale tab apart. Toronto local time. Hidden when unstamped (dev). */
function VersionBadge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch("/version.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.version) setInfo(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!info?.version) return null;
  let when = null;
  if (info.built_at) {
    const s = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Toronto", month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
    }).format(new Date(info.built_at));
    when = s.replace("a.m.", "AM").replace("p.m.", "PM").replace(/, (\d{1,2}:)/, " at $1");
  }
  return (
    <p className="text-right font-mono text-[9px] uppercase leading-tight tracking-widest text-white/35" data-testid="app-version-badge" title="The build this session has loaded">
      Version {info.version}{when && <><br className="sm:hidden" /><span className="hidden sm:inline"> | </span>Updated {when}</>}
    </p>
  );
}

const StatsTab = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [ytd, setYtd] = useState(null); // yearly pay stats: app payrolls + imported Knit stubs
  const [importedStubs, setImportedStubs] = useState([]); // my scanned Knit stubs (downloadable)
  const [loading, setLoading] = useState(true);
  const [vacHistoryOpen, setVacHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cardExpanded, setCardExpanded] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    // silent = background poll: update the numbers in place, never blank the
    // page behind a loading spinner (owner: "update just that field").
    if (!silent) setLoading(true);
    try {
      const [s, e, h] = await Promise.all([
        api.get("/me/stats"),
        api.get("/me/employee"),
        api.get("/me/stats/history?weeks=4"),
      ]);
      setStats(s.data);
      setEmployee(e.data.employee);
      setHistory(h.data || []);
    } catch (err) {
      // 404 = no employee linked to this account. That's an expected state
      // for admins / new users, not an error worth surfacing. Silent polls
      // never toast — stale numbers stay until the next successful tick.
      if (!silent && err?.response?.status !== 404) {
        toast.error(err?.response?.data?.detail || "Could not load your stats");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveTick(useCallback(() => load(true), [load])); // impersonation live-watch: silent refetch on every tick
  // Past pay periods list shows SAVED payrolls only — live drafts are
  // unfinished weeks, not pay records, and must never look downloadable.
  const savedWeeks = useMemo(() => history.filter((r) => r.source === "saved"), [history]);
  // Yearly pay stats (app payrolls + imported Knit stubs) once we know the
  // linked employee. Silent on failure — the card simply stays hidden.
  useEffect(() => {
    if (!employee?.employee_id) return;
    (async () => {
      try {
        const r = await api.get(`/payroll/ytd/${employee.employee_id}`);
        setYtd(r.data);
      } catch { /* not linked / not allowed — hide the card */ }
      try {
        const r = await api.get(`/payroll/imported-paystubs/${employee.employee_id}`);
        setImportedStubs(r.data.stubs || []);
      } catch { /* hide the stub inbox */ }
    })();
  }, [employee?.employee_id]);

  /** Download one imported Knit stub as a real PDF (CSV imports are rendered). */
  const downloadImportedStub = async (s) => {
    try {
      const res = await api.get(`/payroll/imported-paystubs/${employee.employee_id}/${s.import_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const name = `${(s.label || "pay-stub").replace(/\.(csv|pdf)$/i, "")}.pdf`;
      // iOS ignores the download attribute and would hand an untyped blob to Numbers — open the PDF viewer instead.
      if (/iP(hone|ad|od)/.test(navigator.userAgent)) {
        window.open(url, "_blank", "noopener");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not download that pay stub");
    }
  };
  // Auto-refresh stats every 60s so "This week hours" and "Gross this week"
  // count live by the minute while the employee is on the clock (the server
  // swaps today's stored quarter-snapped rows for the running paid total).
  // Only for employees whose time clock is on — owners, salary staff without
  // punch cards, and admins don't need the poll.
  useEffect(() => {
    if (!employee?.time_clock_enabled) return;
    const t = setInterval(() => { load(true); }, 60 * 1000);
    return () => clearInterval(t);
  }, [load, employee?.time_clock_enabled]);

  const startEdit = () => {
    if (!employee) return;
    // Only carry editable keys into the form state.
    const editable = {};
    EDIT_GROUPS.forEach((g) => g.fields.forEach(([k]) => { editable[k] = employee[k] ?? ""; }));
    setForm(editable);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== undefined)
      );
      const { data } = await api.put("/me/employee", payload);
      setEmployee(data.employee);
      toast.success("Saved");
      setEditing(false);
      // Refresh stats too (name/photo/etc may show in header).
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const initials = useMemo(() => {
    const n = (stats?.name || employee?.name || "").trim();
    if (!n) return "?";
    return n.split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  }, [stats, employee]);

  const [downloading, setDownloading] = useState("");
  const [emailing, setEmailing] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [paystubsExpanded, setPaystubsExpanded] = useState(false);
  // Sensitive field reveal (Direct-deposit / SIN). Only the account holder,
  // super-admin, and owner get the eyeball button; the backend returns 403
  // for anyone else who tries the /sensitive endpoint.
  const SENSITIVE_KEYS = useMemo(() => new Set(["sin", "license_number", "bank_transit", "bank_institution", "bank_account"]), []);
  const canReveal = user?.role === "superadmin" || user?.role === "owner" || (employee && user?.linked_employee_id === employee?.employee_id);
  // Plaintext values (fetched once, cached) + which groups are currently in
  // "reveal" mode. Each Reveal button toggles only its own group.
  const [sensitiveData, setSensitiveData] = useState(null);
  const [revealedGroups, setRevealedGroups] = useState(() => new Set());
  const [revealing, setRevealing] = useState("");
  const fieldsForGroup = (group) => (group?.fields || []).map(([k]) => k).filter((k) => SENSITIVE_KEYS.has(k));
  const isRevealed = useCallback((k) => {
    for (const title of revealedGroups) {
      const g = EDIT_GROUPS.find((x) => x.title === title);
      if (g && fieldsForGroup(g).includes(k)) return true;
    }
    return false;
  }, [revealedGroups]);
  const toggleReveal = useCallback(async (group) => {
    const title = group.title;
    const keys = fieldsForGroup(group);
    // Hide → drop from set, restore masked placeholders in the form if editing.
    if (revealedGroups.has(title)) {
      setRevealedGroups((prev) => {
        const next = new Set(prev);
        next.delete(title);
        return next;
      });
      if (editing && employee) {
        setForm((s) => {
          const nxt = { ...s };
          keys.forEach((k) => { nxt[k] = employee[k] ?? ""; });
          return nxt;
        });
      }
      return;
    }
    if (!employee?.employee_id) return;
    // Fetch plaintext once and cache — subsequent groups just flip the flag.
    let data = sensitiveData;
    if (!data) {
      setRevealing(title);
      try {
        const resp = await api.get(`/employees/${employee.employee_id}/sensitive`);
        data = resp.data || {};
        setSensitiveData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Cannot reveal");
        setRevealing("");
        return;
      } finally {
        setRevealing("");
      }
    }
    setRevealedGroups((prev) => {
      const next = new Set(prev);
      next.add(title);
      return next;
    });
    if (editing) {
      setForm((s) => {
        const nxt = { ...s };
        keys.forEach((k) => {
          if (data && k in data) nxt[k] = data[k] || "";
        });
        return nxt;
      });
    }
  }, [revealedGroups, employee, editing, sensitiveData]);
  const [cropSrc, setCropSrc] = useState(null);
  const [photoZoom, setPhotoZoom] = useState(false); // full-screen photo viewer
  const [waiverOpen, setWaiverOpen] = useState(false);

  const onPickPhoto = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    // Turn the picked file into a data URL, then open the same crop+zoom
    // dialog the admin employee editor uses.
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.onerror = () => toast.error("Could not read image");
    reader.readAsDataURL(file);
  };

  const onCropConfirm = async (_file, dataUrl) => {
    setCropSrc(null);
    setPhotoBusy(true);
    try {
      const { data } = await api.post("/me/employee/photo", { data_url: dataUrl });
      setEmployee(data.employee);
      toast.success("Photo updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload photo");
    } finally { setPhotoBusy(false); }
  };

  const onRemovePhoto = async () => {
    setPhotoBusy(true);
    try {
      const { data } = await api.delete("/me/employee/photo");
      setEmployee(data.employee);
      toast.success("Photo removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove photo");
    } finally { setPhotoBusy(false); }
  };

  const downloadPaystub = async (weekStart) => {
    setDownloading(weekStart);
    try {
      const res = await api.get(`/me/paystub/pdf?week_start=${weekStart}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paystub_${(stats?.name || "employee").replace(/\s+/g, "_")}_${weekStart}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download paystub");
    } finally { setDownloading(""); }
  };

  const emailPaystub = async (weekStart) => {
    setEmailing(weekStart);
    try {
      const { data } = await api.post("/me/paystub/email", { week_start: weekStart });
      toast.success(`Emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email paystub");
    } finally { setEmailing(""); }
  };

  if (loading) {
    return <p className="p-6 font-mono text-xs uppercase tracking-widest text-white/40" data-testid="stats-loading">Loading your stats…</p>;
  }
  if (!stats || !employee) {
    // Not linked to an employee record (e.g. superadmin or a brand-new user).
    // Show a friendly welcome instead of a hard error.
    const displayName = user?.name || user?.email?.split("@")[0] || "there";
    const isAdmin = user?.role === "superadmin" || user?.role === "admin";
    // Only the "owner" role (Colin/Brett) sees the tally block on Home.
    // Superadmin still sees + manages it on the Expenses tab.
    return (
      <div className="space-y-4 p-4 sm:p-6" data-testid="stats-tab">
        <div className="mx-auto max-w-3xl rounded-lg border border-white/10 bg-white/[0.03] p-6 sm:p-10">
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">{peekBranding()?.app_name || "Elite Mechanical"}</p>
            <div className="flex items-center gap-3">
              <VersionBadge />
              <NotificationBell testid="welcome-notifications" />
            </div>
          </div>
          <h2 className="mt-1 text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl" data-testid="stats-welcome">
            Welcome back, {displayName}
          </h2>
          <p className="mt-3 max-w-xl text-sm text-white/60">
            {isAdmin
              ? "You're not linked to an employee record — that's normal for admin accounts. Use the tabs above to jump into Projects, Payroll, Employees or Expenses."
              : "Your stats will appear here as soon as an admin links you to your employee record. In the meantime you can still access the tabs your role allows."}
          </p>
          {isAdmin && (
            <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-white/30">
              Tip · Admin Settings → Users to link a user to an employee.
            </p>
          )}
        </div>

      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="stats-tab">
      {/* Header — identity */}
      <div className="relative rounded-lg border border-white/10 bg-white/[0.03] p-4 pb-3">
        {/* version + bell in normal flow (never absolutely positioned — a
            wide version stamp overlapped the name on phones when it was) */}
        <div className="mb-2 flex items-center justify-end gap-3" data-testid="stats-header-actions">
          <VersionBadge />
          <NotificationBell testid="stats-notifications" />
        </div>
        <div className="flex flex-wrap items-center gap-4">
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPhotoZoom(true)}
            title="View photo"
            aria-label="View photo"
            data-testid="stats-photo-zoom"
            className="h-16 w-16 overflow-hidden rounded-full border border-white/15 bg-white/5 transition-transform hover:scale-[1.04]"
          >
            {employee.photo ? (
              <img src={employee.photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-mono text-xl text-white/60">{initials}</div>
            )}
          </button>
          {/* edit — under the profile picture */}
          <button
            onClick={editing ? () => setEditing(false) : startEdit}
            data-testid={editing ? "stats-cancel-edit" : "stats-edit-info"}
            aria-label={editing ? "Cancel" : "Edit my info"}
            title={editing ? "Cancel" : "Edit my info"}
            className="inline-flex items-center justify-center rounded-md border border-white/15 bg-white/5 p-2 text-white/80 hover:border-white/30 hover:text-white"
          >
            {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">My stats</p>
          <h2 className="truncate text-2xl font-bold uppercase tracking-tight text-white">{stats.name || "—"}</h2>
          <p className="font-mono text-[11px] text-white/50">
            {stats.job_title || "—"}{stats.trade ? ` · ${stats.trade}` : ""}
            {stats.employment_type ? ` · ${stats.employment_type.replace("_", " ")}` : ""}
            {stats.pay_type ? ` · ${stats.pay_type}` : ""}
          </p>
          {stats.hired_at && (
            <p className="font-mono text-[10px] text-white/40">Employed since {stats.hired_at}</p>
          )}
        </div>
        </div>

        {/* Certificates — compact thumbnail strip attached to the card */}
        <CertificatesStrip />
      </div>

      {/* Photo viewer — tap the profile photo to view full-screen, swap or remove */}
      {photoZoom && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => setPhotoZoom(false)} data-testid="stats-photo-viewer">
          <div
            className="w-full max-w-sm overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">{stats.name || "Profile photo"}</p>
              <button type="button" onClick={() => setPhotoZoom(false)} aria-label="Close" data-testid="stats-photo-viewer-close" className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-center bg-black/30 p-6">
              {employee.photo ? (
                <img src={employee.photo} alt={stats.name || ""} className="max-h-[55vh] w-auto max-w-full rounded-lg object-contain" data-testid="stats-photo-viewer-img" />
              ) : (
                <div className="flex h-40 w-40 items-center justify-center rounded-full border border-white/15 bg-white/5 font-mono text-5xl text-white/60">{initials}</div>
              )}
            </div>
            <div className="flex items-center justify-center gap-2 border-t border-white/10 px-4 py-3">
              <label
                className={`inline-flex cursor-pointer items-center gap-2 rounded-md bg-[#2ca01c] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#248015] ${photoBusy ? "pointer-events-none opacity-50" : ""}`}
                data-testid="stats-photo-viewer-upload-label"
              >
                <Camera className="h-3.5 w-3.5" />
                {photoBusy ? "Uploading…" : (employee.photo ? "Change photo" : "Upload photo")}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="stats-photo-viewer-file"
                  onChange={(ev) => { setPhotoZoom(false); onPickPhoto(ev.target.files?.[0]); }}
                />
              </label>
              {employee.photo && (
                <button
                  onClick={() => { setPhotoZoom(false); onRemovePhoto(); }}
                  disabled={photoBusy}
                  data-testid="stats-photo-viewer-remove"
                  className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:border-red-500/60 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Time clock — clock in / lunch / out with GPS */}
      <ClockCard />

      {/* New-hire checklist — disappears once every required step is done */}
      <OnboardingCard />

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {user?.role !== "owner" && (
          <StatBox
            testid="stat-hours"
            label="This week hours"
            value={hrs(stats.hours_this_week)}
            sub={`week of ${stats.week_start}${stats.hours_live ? " · live" : ""}${stats.stat_holiday_hours ? ` · incl. ${hrs(stats.stat_holiday_hours)} stat` : ""}`}
          />
        )}
        <StatBox
          testid="stat-gross"
          label="Gross this week"
          value={money(stats.gross_this_week)}
          sub={stats.overtime_hours ? `${hrs(stats.overtime_hours)} OT` : `${hrs(stats.regular_hours)} reg`}
        />
        <StatBox
          testid="stat-net"
          label="Net this week"
          value={money(stats.net_this_week)}
          sub="est. take-home"
          tint="border-emerald-800/40 bg-emerald-800/10"
        />
        {user?.role !== "owner" && (
          <StatBox
            testid="stat-vacation"
            label="Vacation banked"
            value={money(stats.vacation_available)}
            sub={`accrued ${money(stats.vacation_accrued)} · paid ${money(stats.vacation_paid_out)}`}
            tint="border-[#1a3fd1]/40 bg-[#1a3fd1]/10"
            action={
              <button type="button" onClick={() => setVacHistoryOpen(true)} data-testid="stat-vacation-history-btn"
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white/80 transition-colors hover:border-white/30 hover:text-white">
                <History className="h-3 w-3" /> Week-by-week history
              </button>
            }
          />
        )}
        {user?.role !== "owner" && (
          <StatBox
            testid="stat-sick-days"
            label="Sick days used"
            value={String(stats.sick_days_ytd ?? 0)}
            sub={`${stats.sick_days_total ?? 0} all-time`}
            tint="border-amber-700/40 bg-amber-700/10"
          />
        )}
        {user?.role !== "owner" && (
          <StatBox
            testid="stat-days-off"
            label="Days booked off"
            value={String(stats.days_off_ytd ?? 0)}
            sub={`${stats.days_off_total ?? 0} all-time`}
            tint="border-sky-700/40 bg-sky-700/10"
          />
        )}
        {user?.role !== "owner" && (
          <StatBox
            testid="stat-no-show-days"
            label="No-shows"
            value={String(stats.no_show_days_ytd ?? 0)}
            sub="this year"
            tint="border-red-700/40 bg-red-700/10"
          />
        )}
      </div>

      {/* Trend chart — last 4 weeks */}
      {history.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-history-chart">
          <div className="mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-white/40" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Last 4 weeks — hours & pay</h3>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={history.map((r) => ({
                week: r.week_start.slice(5),  // MM-DD
                hours: r.hours,
                gross: r.gross,
                net: r.net,
              }))} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(127,127,127,0.2)" />
                <XAxis dataKey="week" tick={{ fill: "#6b7280", fontSize: 11, fontFamily: "monospace" }} />
                <YAxis yAxisId="left" tick={{ fill: "#6b7280", fontSize: 11, fontFamily: "monospace" }} width={40} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: "#6b7280", fontSize: 11, fontFamily: "monospace" }} width={54} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 12, color: "#fff" }}
                  labelStyle={{ color: "#8fa4f0", fontFamily: "monospace" }}
                  formatter={(v, name) => {
                    // Recharts passes the series `name` prop here (not the
                    // dataKey), so match on the capitalized labels.
                    if (name === "Hours") return [`${v} h`, "Hours"];
                    const money = `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    return [money, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace", color: "#6b7280" }} />
                <Bar yAxisId="left" dataKey="hours" fill="#1a3fd1" name="Hours" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Line yAxisId="right" type="monotone" dataKey="gross" stroke="#8fa4f0" strokeWidth={2} dot={{ r: 3 }} name="Gross" />
                <Line yAxisId="right" type="monotone" dataKey="net" stroke="#3fb984" strokeWidth={2} dot={{ r: 3 }} name="Net" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 font-mono text-[10px] text-white/30">
            Values for weeks without a saved paystub are live drafts based on your current rate.
          </p>
        </div>
      )}

      {/* Year-to-date pay — this app's payrolls PLUS any Knit pay stubs the
          office imported, so the full year shows in one place. */}
      {ytd && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-ytd-card">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-white/40" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">My {ytd.year} pay — year to date</h3>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
            {[
              ["Gross so far", money(ytd.totals.gross), "stats-ytd-gross"],
              ["Net (take-home)", money(ytd.totals.net), "stats-ytd-net"],
              ["CPP paid", money(ytd.totals.cpp), "stats-ytd-cpp"],
              ["EI paid", money(ytd.totals.ei), "stats-ytd-ei"],
              ["Federal tax", money(ytd.totals.federal_tax), "stats-ytd-fed"],
              ["ON tax", money(ytd.totals.provincial_tax), "stats-ytd-on"],
              ["Vacation pay", money(ytd.totals.vacation_pay), "stats-ytd-vac"],
              ["Hours worked", `${Number(ytd.totals.hours_worked || 0).toFixed(2)} h`, "stats-ytd-hours"],
            ].map(([label, val, tid]) => (
              <div key={tid}>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</p>
                <p className="font-mono text-lg font-bold text-white" data-testid={tid}>{val}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-[10px] text-white/35" data-testid="stats-ytd-sources">
            {ytd.app.stubs > 0 && <>From {ytd.app.stubs} pay run{ytd.app.stubs === 1 ? "" : "s"} in this app</>}
            {ytd.app.stubs > 0 && ytd.imported.stubs > 0 && " · "}
            {ytd.imported.stubs > 0 && <>plus {ytd.imported.stubs} imported pay stub{ytd.imported.stubs === 1 ? "" : "s"} through {ytd.imported.through}</>}
            {ytd.app.covered_by_import > 0 && <> · {ytd.app.covered_by_import} app pay run{ytd.app.covered_by_import === 1 ? "" : "s"} already covered by imported stubs — not double-counted</>}
            {ytd.app.stubs === 0 && ytd.imported.stubs === 0 && "No pay on file yet this year."}
          </p>
        </div>
      )}

      {/* Imported pay stubs (from the previous payroll system) — the employee
          can open each PDF and verify the amounts counted in the yearly card. */}
      {importedStubs.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-imported-stubs">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-white/40" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">My imported pay stubs</h3>
          </div>
          <p className="mt-1 font-mono text-[10px] text-white/35">
            From the previous payroll system — these amounts are included in your yearly totals above.
          </p>
          <div className="mt-2 space-y-1.5">
            {importedStubs.map((s) => (
              <div key={s.import_id}
                className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2"
                data-testid={`stats-imported-stub-${s.import_id}`}>
                <div className="min-w-0">
                  <p className="font-mono text-xs font-semibold text-white">Pay date {s.pay_date}</p>
                  <p className="font-mono text-[10px] text-white/40">
                    {s.period_start && s.period_end ? `${s.period_start} → ${s.period_end} · ` : ""}
                    gross ${Number(s.gross || 0).toFixed(2)} · net ${Number(s.net || 0).toFixed(2)}
                  </p>
                </div>
                <button onClick={() => downloadImportedStub(s)}
                  data-testid={`stats-imported-stub-dl-${s.import_id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white">
                  <Download className="h-3.5 w-3.5" /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past pay periods — PDF download. Only the most recent shows by
          default; older weeks tuck behind a "Show more" toggle. */}
      {savedWeeks.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-paystub-list">
          <div className="mb-3 flex items-center gap-2">
            <Download className="h-4 w-4 text-white/40" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Past pay periods · download PDF</h3>
          </div>
          <div className="divide-y divide-white/5 overflow-hidden rounded border border-white/10">
            {(() => {
              const rows = [...savedWeeks].reverse();
              const visible = paystubsExpanded ? rows : rows.slice(0, 1);
              return visible.map((r) => (
              <div key={r.week_start}
                data-testid={`paystub-row-${r.week_start}`}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="font-mono text-[11px] text-white/90">Week of {r.week_start}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {r.hours.toFixed(2)} h
                    {r.rate > 0 && (
                      <span className="text-white/60" data-testid={`paystub-rate-${r.week_start}`}>
                        {" "}@ ${r.rate.toFixed(2)}/h{r.perfect_attendance ? " · bonus rate" : ""}
                      </span>
                    )}
                    {" · "}gross ${r.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · net ${r.net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-[9px] ${r.source === "saved" ? "bg-emerald-800/30 text-emerald-800 dark:text-emerald-300" : "bg-white/10 text-white/50"}`}>
                      {r.source}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => emailPaystub(r.week_start)}
                    disabled={emailing === r.week_start}
                    data-testid={`paystub-email-${r.week_start}`}
                    title={`Email PDF to ${employee?.email || "your inbox"}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {emailing === r.week_start ? "Sending…" : "Email me"}
                  </button>
                  <button
                    onClick={() => downloadPaystub(r.week_start)}
                    disabled={downloading === r.week_start}
                    data-testid={`paystub-download-${r.week_start}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {downloading === r.week_start ? "Preparing…" : "PDF"}
                  </button>
                </div>
              </div>
              ));
            })()}
          </div>
          {savedWeeks.length > 1 && (
            <button
              type="button"
              onClick={() => setPaystubsExpanded((v) => !v)}
              data-testid="paystub-show-more"
              className="mt-2 w-full rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/60 hover:border-white/25 hover:text-white"
            >
              {paystubsExpanded ? "Show less" : `Show ${savedWeeks.length - 1} more`}
            </button>
          )}
        </div>
      )}

      {/* Vacation-pay opt-out (self-service) — hidden for owners (salary, N/A) */}
      {user?.role !== "owner" && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="stats-vacation-waiver">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Vacation pay</h3>
            <p className="mt-1 text-sm text-white/80">
              {employee.vacation_opt_out
                ? "You've opted out of ESA vacation pay. No accrual, no payout."
                : "You're currently accruing Ontario ESA vacation pay."}
            </p>
            {employee.vacation_waiver?.signed_at && (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-emerald-800 dark:text-emerald-300">
                Signed waiver on file · {employee.vacation_waiver.signed_at.slice(0, 10)}
              </p>
            )}
          </div>
          <button
            onClick={() => setWaiverOpen(true)}
            data-testid="stats-vacation-waiver-btn"
            className={`shrink-0 rounded-md border px-3 py-2 text-xs font-semibold ${employee.vacation_opt_out ? "border-white/15 bg-white/5 text-white/80 hover:border-white/25" : "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-[#a9baf5] hover:border-[#1a3fd1] hover:bg-[#1a3fd1]/25 hover:text-white"}`}
          >
            {employee.vacation_opt_out ? "View / revoke waiver" : "Opt out & sign waiver"}
          </button>
        </div>
      </div>
      )}

      {/* Editable / read-only info */}
      {!editing ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="mb-3 flex items-center gap-2">
            <UserIcon className="h-4 w-4 text-white/40" />
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">My employee card</h3>
          </div>
          {EDIT_GROUPS.filter((_, gi) => gi === 0 || cardExpanded).map((g, idx) => {
            const groupHasSensitive = g.fields.some(([k]) => SENSITIVE_KEYS.has(k));
            const shown = revealedGroups.has(g.title);
            const busy = revealing === g.title;
            return (
            <div key={g.title} className={idx === 0 ? "" : "mt-5 border-t border-white/10 pt-4"} data-testid={`stat-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]">{g.title}</h4>
                {groupHasSensitive && canReveal && (
                  <button
                    type="button"
                    onClick={() => toggleReveal(g)}
                    disabled={!!revealing}
                    data-testid={`reveal-sensitive-btn-${g.title.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={shown ? "Hide sensitive" : "Reveal sensitive"}
                    title={shown ? "Hide" : "Reveal"}
                    className="flex items-center gap-1 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
                  >
                    {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {busy ? "…" : (shown ? "Hide" : "Reveal")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                {g.fields.map(([k, label]) => {
                  const raw = employee[k];
                  const showPlain = SENSITIVE_KEYS.has(k) && shown && sensitiveData && sensitiveData[k];
                  const value = showPlain ? sensitiveData[k] : raw;
                  return (
                    <div key={k} data-testid={`stat-view-${k}`} className="flex justify-between gap-3 border-b border-white/5 py-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</span>
                      <span className={`truncate text-right text-sm ${showPlain ? "text-emerald-200" : "text-white/90"}`}>{value || "—"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}

          {/* Admin-managed fields — visible but not editable (collapsed with the rest) */}
          {cardExpanded && READ_ONLY_GROUPS.map((g) => (
            <div key={g.title} className="mt-5 border-t border-white/10 pt-4">
              <h4 className="mb-2 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]">{g.title}</h4>
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {g.fields.map(([k, label, fmt]) => {
                  const raw = employee[k];
                  const shown = typeof fmt === "function" ? fmt(raw) : (raw || "—");
                  return (
                    <div key={k} data-testid={`stat-view-${k}`} className="flex justify-between gap-3 border-b border-white/5 py-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</span>
                      <span className="truncate text-right text-sm text-white/70">{shown || "—"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setCardExpanded((v) => !v)}
            data-testid="employee-card-toggle"
            className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50 transition-colors hover:border-white/20 hover:text-white/80"
          >
            {cardExpanded ? (<><ChevronUp className="h-3 w-3" /> Show less</>) : (<><ChevronDown className="h-3 w-3" /> Show more</>)}
          </button>
        </div>
      ) : (
        <div className="space-y-5 rounded-lg border border-white/10 bg-white/[0.02] p-4">
          {/* Profile photo — first thing in the edit form so it's obvious */}
          <div>
            <h4 className="mb-2 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]">Profile photo</h4>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-white/15 bg-white/5">
                {employee.photo ? (
                  <img src={employee.photo} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center font-mono text-2xl text-white/50">{initials}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <label
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-[#1a3fd1] hover:text-white ${photoBusy ? "pointer-events-none opacity-50" : ""}`}
                  data-testid="stat-photo-upload-label"
                >
                  <Camera className="h-3.5 w-3.5" />
                  {photoBusy ? "Uploading…" : (employee.photo ? "Change photo" : "Upload photo")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="stat-photo-file-input"
                    onChange={(ev) => onPickPhoto(ev.target.files?.[0])}
                  />
                </label>
                {employee.photo && (
                  <button
                    onClick={onRemovePhoto}
                    disabled={photoBusy}
                    data-testid="stat-photo-remove"
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-300 hover:border-red-500/50 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
            <p className="mt-2 font-mono text-[10px] text-white/40">
              JPG or PNG. Zoom & drag to pick the crop, then save.
            </p>
          </div>

          {EDIT_GROUPS.map((g) => {
            const groupHasSensitive = g.fields.some(([k]) => SENSITIVE_KEYS.has(k));
            const shown = revealedGroups.has(g.title);
            const busy = revealing === g.title;
            return (
            <div key={g.title}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]">{g.title}</h4>
                {groupHasSensitive && canReveal && (
                  <button
                    type="button"
                    onClick={() => toggleReveal(g)}
                    disabled={!!revealing}
                    data-testid={`reveal-sensitive-edit-btn-${g.title.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={shown ? "Hide sensitive" : "Reveal sensitive"}
                    title={shown ? "Hide" : "Reveal to edit"}
                    className="flex items-center gap-1 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
                  >
                    {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {busy ? "…" : (shown ? "Hide" : "Reveal to edit")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {g.fields.map(([k, label, type]) => {
                  const locked = SENSITIVE_KEYS.has(k) && !isRevealed(k);
                  return (
                  <div key={k}>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</label>
                    <input
                      type={type || "text"}
                      value={form[k] ?? ""}
                      onChange={(ev) => setForm((s) => ({ ...s, [k]: ev.target.value }))}
                      data-testid={`stat-edit-${k}`}
                      readOnly={locked}
                      onFocus={(ev) => {
                        if (locked && form[k]?.endsWith?.("*******")) {
                          ev.target.blur();
                          toggleReveal(g);
                        }
                      }}
                      className={`mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none ${locked ? "cursor-pointer text-white/60" : ""}`}
                    />
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70"
              data-testid="stat-edit-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="stat-edit-save"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      <PhotoCropDialog
        open={!!cropSrc}
        src={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={onCropConfirm}
      />

      <VacationBankHistoryDialog
        open={vacHistoryOpen}
        mine
        employeeName={employee?.name}
        onClose={() => setVacHistoryOpen(false)}
      />

      <VacationWaiverDialog
        open={waiverOpen}
        employee={employee}
        endpoints={{
          sign: "/me/vacation-waiver",
          revoke: "/me/vacation-waiver",
          pdf: "/me/vacation-waiver/pdf",
        }}
        onClose={() => setWaiverOpen(false)}
        onSaved={(emp) => setEmployee(emp)}
      />
    </div>
  );
};

export default StatsTab;
